import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  COST_SCOPE,
  DEFAULT_FINANCE_COST_SETTINGS,
  FINANCE_COLLECTIONS,
  FINANCE_SETTINGS_DOC
} from './financeCostConstants';

export function financeRulesCol(sube) {
  return collection(db, 'subeler', sube, FINANCE_COLLECTIONS.RULES);
}

export function financeAuditCol(sube) {
  return collection(db, 'subeler', sube, FINANCE_COLLECTIONS.RULE_AUDIT);
}

export function financeSettingsRef(sube) {
  return doc(db, 'subeler', sube, FINANCE_COLLECTIONS.SETTINGS, FINANCE_SETTINGS_DOC);
}

export async function loadFinanceCostSettings(sube) {
  if (!sube) return { ...DEFAULT_FINANCE_COST_SETTINGS };
  const snap = await getDoc(financeSettingsRef(sube));
  if (!snap.exists()) return { ...DEFAULT_FINANCE_COST_SETTINGS, id: FINANCE_SETTINGS_DOC };
  return { ...DEFAULT_FINANCE_COST_SETTINGS, ...snap.data(), id: snap.id };
}

export async function saveFinanceCostSettings(sube, patch, personel) {
  const ref = financeSettingsRef(sube);
  const prev = await loadFinanceCostSettings(sube);
  await setDoc(ref, {
    ...prev,
    ...patch,
    sube,
    updatedAt: serverTimestamp(),
    updatedBy: personel
      ? { id: personel.id || null, isim: personel.isim || '', soyisim: personel.soyisim || '' }
      : null
  }, { merge: true });
  return { ...prev, ...patch };
}

export async function loadFinanceCostRules(sube) {
  if (!sube) return [];
  const snap = await getDocs(financeRulesCol(sube));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function personelStamp(personel) {
  return personel
    ? { id: personel.id || null, isim: personel.isim || '', soyisim: personel.soyisim || '' }
    : null;
}

function summarizeRuleChange(before, after) {
  const parts = [];
  if (!before) {
    parts.push(`Yeni kural: ${after?.name || after?.scopeType}`);
    return parts.join(' · ');
  }
  if (before.goldRateSource !== after.goldRateSource) {
    parts.push(`Has kaynağı: ${before.goldRateSource || '—'} → ${after.goldRateSource || '—'}`);
  }
  if (before.costMethod !== after.costMethod) {
    parts.push(`Yöntem: ${before.costMethod || '—'} → ${after.costMethod || '—'}`);
  }
  if (before.manualCostTry !== after.manualCostTry) {
    parts.push('Manuel maliyet değişti');
  }
  if (before.active !== after.active) {
    parts.push(after.active === false ? 'Kural pasif' : 'Kural aktif');
  }
  if (!parts.length) parts.push('Kural güncellendi');
  return parts.join(' · ');
}

export async function writeFinanceCostRuleAudit(sube, { ruleId, before, after, personel }) {
  await addDoc(financeAuditCol(sube), {
    ruleId: ruleId || after?.id || null,
    before: before || null,
    after: after || null,
    summary: summarizeRuleChange(before, after),
    changedBy: personelStamp(personel),
    changedAt: serverTimestamp(),
    sube
  });
}

export function buildCostRulePayload(input, { sube, personel, previous } = {}) {
  const version = (Number(previous?.version) || 0) + 1;
  return {
    scopeType: input.scopeType || COST_SCOPE.CATEGORY,
    scopeId: String(input.scopeId || '').trim(),
    name: String(input.name || '').trim(),
    category: input.category || null,
    costMethod: input.costMethod,
    goldRateSource: input.goldRateSource || null,
    manualGoldRate: input.manualGoldRate ?? null,
    manualCostTry: input.manualCostTry ?? null,
    unitCostTry: input.unitCostTry ?? null,
    costRate: input.costRate ?? input.manualCostRate ?? null,
    laborModel: input.laborModel || 'inherit',
    laborValueTry: input.laborValueTry ?? null,
    milyemOverride: (() => {
      const n = Number(input.milyemOverride);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    hamCode: input.hamCode ? String(input.hamCode).trim() : null,
    hamMultiplier: (() => {
      const n = Number(input.hamMultiplier);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    extraFactor: (() => {
      const n = Number(input.extraFactor);
      return Number.isFinite(n) && n > 0 ? n : null;
    })(),
    additionalCosts: Array.isArray(input.additionalCosts) ? input.additionalCosts : [],
    active: input.active !== false,
    priority: Number(input.priority) || 0,
    version,
    sube,
    createdAt: previous?.createdAt || serverTimestamp(),
    createdBy: previous?.createdBy || personelStamp(personel),
    updatedAt: serverTimestamp(),
    updatedBy: personelStamp(personel)
  };
}

export async function saveFinanceCostRule(sube, input, personel, existingId) {
  const col = financeRulesCol(sube);
  let previous = null;
  if (existingId) {
    const prevSnap = await getDoc(doc(col, existingId));
    previous = prevSnap.exists() ? { id: prevSnap.id, ...prevSnap.data() } : null;
  }
  const payload = buildCostRulePayload(input, { sube, personel, previous });
  if (existingId) {
    await setDoc(doc(col, existingId), payload, { merge: true });
    const after = { id: existingId, ...payload };
    await writeFinanceCostRuleAudit(sube, { ruleId: existingId, before: previous, after, personel });
    return after;
  }
  const created = await addDoc(col, payload);
  const after = { id: created.id, ...payload };
  await writeFinanceCostRuleAudit(sube, { ruleId: created.id, before: null, after, personel });
  return after;
}

export async function loadFinanceCostRuleAudit(sube, { ruleId, limitCount = 40 } = {}) {
  const snap = await getDocs(financeAuditCol(sube));
  let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (ruleId) rows = rows.filter((r) => r.ruleId === ruleId);
  rows.sort((a, b) => {
    const am = a.changedAt?.toMillis?.() || 0;
    const bm = b.changedAt?.toMillis?.() || 0;
    return bm - am;
  });
  return rows.slice(0, limitCount);
}
